import { mapGetters, mapState } from 'vuex'
import numeral from 'numeral'
import 'numeral/locales'
import _isEmpty from 'lodash/isEmpty'
import _isObject from 'lodash/isObject'
import _includes from 'lodash/includes'
import { _property } from './methods.js'
import * as xss from 'xss'
import { delCookie } from '@/util/methods'

const config = require('~/config')
const { api, initials, currencySymbol, hosts, hntDomain, siteId, deployEnv } = config
const images = require.context('@/assets/img/localize', true, /\.(png|jpe?g|gif)(\?.*)?$/)
const imageKeys = images.keys()

if (numeral.locales['ko'] === undefined) {
  numeral.register('locale', 'ko', {
    delimiters: {
      thousands: ',',
      decimal: '.',
    },
    abbreviations: {
      thousand: '천',
      million: '만',
      billion: '십억',
      trillion: '조',
    },
    ordinal: function (number) {
      return '째'
    },
    currency: {
      symbol: '₩',
    },
  })
}

const mixins = {
  computed: {
    ...mapState({
      isLogged: ({ auth }) => auth.isLogged,
      loggedUser: ({ auth }) => auth.loggedUser,
      cities: ({ cities }) => cities,
      onLive: ({ onLive }) => onLive,
    }),
    ...mapGetters({
      currency: 'i18n/currency',
      locale: 'i18n/locale',
    }),
  },
  methods: {
    // TODO: 외부에서 사용하는 method에 접두사 붙여주자, 시작은 safe함수 부터...
    isMt: _isEmpty,
    $_globalMixin_safe(html) {
      if (!html) return ''
      return xss(html, {
        onIgnoreTagAttr: (tag, name, value) => {
          if (name === 'style') return `${name}="${value}"`
        },
      })
    },
    get(model, path, _default = '') {
      const getter = _property(path.split('.'))
      return getter(model) || _default
    },
    /**
     * hasKey( obj,'key1.key2');
     * @param obj
     * @param keyStr
     * @returns {boolean}
     * key만 체크
     */
    hasKey(obj, keyStr) {
      if (_isEmpty(obj)) {
        return false
      }
      if (keyStr === undefined) {
        return _isEmpty(obj)
      }

      return !(_property(keyStr.split('.'))(obj) === undefined)
    },
    /**
     * hasKeyValue( Object, 'key1.key2' );
     * @param obj
     * @param keyStr
     * @returns {boolean}
     * key와 해당키의 value의 empty체크
     */
    hasKeyValue(obj, keyStr) {
      if (_isEmpty(obj)) {
        return false
      }
      if (keyStr === undefined) {
        return _isEmpty(obj)
      }

      return !_isEmpty(_property(keyStr.split('.'))(obj))
    },
    getCount(type) {
      // type: cart / alert
      this.$store.dispatch('getCount', type)
    },
    getCountryNameByCityId(id) {
      let countryName, flag
      ;(this.cities.continents || []).every((continent) => {
        if (flag) return false
        ;(continent.countries || []).every((country) => {
          if (flag) return false
          ;(country.cities || []).every((city) => {
            if (city.id == id) {
              flag = true
              countryName = country.name
              return false
            }
            return true
          })
          return true
        })
        return true
      })

      return countryName
    },
    getCountryName(cityName) {
      let countryName, flag
      ;(this.cities.continents || []).every((continent) => {
        if (flag) return false
        ;(continent.countries || []).every((country) => {
          if (flag) return false
          ;(country.cities || []).every((city) => {
            if (city.name == cityName) {
              flag = true
              countryName = country.name
              return false
            }
            return true
          })
          return true
        })
        return true
      })

      return countryName
    },
    getCityByFriendlyPath(path) {
      let cityName, flag
      ;(this.cities.continents || []).every((continent) => {
        if (flag) return false
        ;(continent.countries || []).every((country) => {
          if (flag) return false
          ;(country.cities || []).every((city) => {
            if (city.friendlyPath == path) {
              flag = true
              cityName = city.name
              return false
            }
            return true
          })
          return true
        })
        return true
      })
      return cityName
    },
    getThemeNameByThemeId(themeId) {
      let themeName, flag
      ;(this.themeFilter.location.continents || []).every((continent) => {
        if (flag) return false
        ;(continent.cities || []).every((city) => {
          if (flag) return false
          ;(city.themes || []).every((theme) => {
            if (theme.id == themeId) {
              flag = true
              themeName = theme.name
              return false
            }
            return true
          })
          return true
        })
        return true
      })

      return themeName
    },
    escapeHtml(str) {
      let map = {
        '<': '&lt;',
        '>': '&gt;',
        '(': '&#40;',
        ')': '&#41;',
        '#': '&#35;',
        '&': '&#38;',
        "'": '&#39;',
        '"': '&quot;',
      }
      return str.replace(/[&<>"']/g, function (m) {
        return map[m]
      })
    },
    unescapeHtml(str, toBr = true) {
      if (str == null) {
        return ''
      }
      let newHtml = str
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#40;/g, '(')
        .replace(/&#41;/g, ')')
        .replace(/&#35;/g, '#')
        .replace(/&#38;/g, '&')
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
      if (toBr === true) {
        newHtml = this.lineToBr(newHtml)
      }
      return newHtml
    },
    lineToBr(text) {
      return text.replace(/(?:\r\n|\r|\n|\\n|\\r)/g, '<br/>')
    },
    $$alert(message, options) {
      this.$alert(
        message,
        Object.assign(
          {},
          {
            dangerouslyUseHTMLString: true,
            showClose: false,
            customClass: 'el-message-box--small',
            confirmButtonText: this.$t('common.button.confirm'),
            roundButton: true,
            confirmButtonClass: 'el-button--info',
          },
          options
        )
      )
    },
    $$confirm(message, title, options) {
      if (_isObject(title)) {
        options = title
        title = ''
      }
      this.$confirm(
        message,
        title,
        Object.assign(
          {},
          {
            dangerouslyUseHTMLString: true,
            customClass: 'title-english',
            showClose: true,
            confirmButtonText: this.$t('common.button.confirm'),
            cancelButtonText: this.$t('common.button.cancel'),
            roundButton: true,
            cancelButtonClass: 'el-button--info',
          },
          options
        )
      )
    },
    $c(number, currency = this.currency) {
      let format = currencySymbol[(currency || initials.currency).toLowerCase()]
      return (format[this.locale] || format['default']).replace(
        /\{([0-9.,\[\.\]]+)\}/,
        (match, key) => {
          return numeral(number).format(key)
        }
      )
    },
    floatLength(a) {
      let e = 1
      while (Math.round(a * e) / e !== a) e *= 10
      return Math.log(e) / Math.LN10
    },
    $m(n) {
      n = Number(n)
      let format = '0,0'
      if (n % 1 !== 0) {
        // float
        format = '0,0.' + '0'.repeat(this.floatLength(n))
      }
      return numeral(n).format(format)
    },
    $count(num) {
      numeral.locale(this.locale)

      let value
      let thousand
      let type =
        this.locale == 'ko' ||
        this.locale == 'ja' ||
        this.locale == 'zh-cn' ||
        this.locale == 'zh-tw'

      if (num < 100) {
        value = 100
      } else if (num >= 100 && num < 500) {
        value = 100
      } else if (num >= 500 && num < 1000) {
        value = 500
      } else if (num >= 1000 && num < 5000) {
        value = 1000
      } else if (num >= 5000 && num < 10000) {
        value = 5000
      } else if (num >= 10000 && num < 100000) {
        const numChaAt = num.toString()
        value = numChaAt.charAt(0) + '0000'
        if (type) {
          thousand = numChaAt.charAt(0) + this.$t('common.unit.money.tenThousand')
        }
      } else if (num >= 100000 && num < 1000000) {
        const numChaAt = num.toString()
        value = numChaAt.charAt(0) + '00000'
        if (type) {
          thousand = numChaAt.charAt(0) + '0' + this.$t('common.unit.money.tenThousand')
        }
      } else {
        value = 1000000
        if (type) {
          thousand = '100' + this.$t('common.unit.money.tenThousand')
        }
      }

      return value < 1000 ? value : thousand ? thousand : numeral(value).format('0a')
    },
    shouldLogin() {
      this.$$confirm(this.$t('common.confirm.requireLogin'), {
        callback: (action) => {
          if (action === 'confirm') {
            localStorage.setItem('login_success_path', this.$route.fullPath)
            this.hntLogin()
          }
        },
      })
    },
    hntLogin() {
      const loginSuccessPath = localStorage.getItem('login_success_path')
      localStorage.removeItem('login_success_path')
      const redirectUri = `${location.origin}${loginSuccessPath}`
      const hntLoginUri = `${
        hosts.hanatourAccount
      }?siteId=${siteId}&redirectUri=${encodeURIComponent(redirectUri)}`
      document.domain = hntDomain
      history.replaceState({ loginFlag: true }, null, location.href)
      location.href = hntLoginUri
    },
    publishApiKey() {
      const httpConfig = {
        headers: { siteId },
        withCredentials: true,
      }
      this.$http
        .get(`${hosts.hanatourApi}/chnl/api/apiKey`, httpConfig)
        .then(({ data }) => {
          const apiKey = encodeURIComponent(data.apiKey)
          if (data.success) {
            location.href = `${api.server[deployEnv]}/user/social/hanatour/oauth/callback?code=${apiKey}`
          } else {
            console.log('data.success error : ', data.success)
            //location.href = this.localePath('/'); // apiKey 발급 실패 시 메인으로 이동
          }
        })
        .catch((error) => {
          //console.log('error : ', error);location.href = this.localePath('/'); // apiKey 발급 실패 시 메인으로 이동
          console.log('error : ', error)

          if (error && error.status === 417) {
            this.$store.dispatch('logout').then(() => {
              delCookie('hnt_uid')
              location.href = `${hosts.hanatourAccount}/logout?redirectUri=${hosts.user}`
              return
            })
          }
        })
    },
    membershipVerification() {
      this.$$confirm(
        '해당 서비스는 통합회원만 이용할 수 있습니다.<br>통합회원 전환 후 이용해주세요.',
        {
          confirmButtonText: '통합회원 전환하기',
          cancelButtonText: '나중에 하기',
          closeOnClickModal: false,
          callback: (action) => {
            if (action !== 'confirm') {
              if (this.$route.name !== 'buy') return
              this.$router.go(-1)
            } else {
              const redirectPath = decodeURIComponent(`${hosts.user}${this.$route.fullPath}`)
              location.href = `${hosts.hanatour}/com/lgi/CHPC0LGI0201M200?redirectUri=${redirectPath}`
            }
          },
        }
      )
    },
    toDate(date, format = 'LL') {
      if (date === '********') {
        return this.$t('mypage.ths.maskingBirthday')
      } else {
        return this.$moment(date).format(format)
      }
    },
    localePath(mixins) {
      let locale = this.locale || initials.fallbackLocale
      if (mixins === '/') mixins = ''
      return `/${locale + mixins}`
    },
    /**
     * @description HATs front 상품 상세 페이지로 이동
     * @params {string} path - '/지역코드/상품코드' 조합된 문자열
     */
    $_globalMixin_navigateToProductDetail(path) {
      try {
        if (!path || !path.includes('/')) {
          throw new Error('invalid path')
        }
        window.location.href = `${hosts.user}/product-detail/${path}`
      } catch (err) {
        console.log('상품상세 페이지로 이동 중 오류 발생', err)
      }
    },
    localImage(file) {
      return file && _includes(imageKeys, `./${file}`) ? images(`./${file}`) : ''
    },

    highlight(text, keyword) {
      return text.replace(
        new RegExp(`(${keyword.replace(/\s/g, '').split('').join('\\s*')})`, 'gim'),
        '<span class="highlight">$1</span>'
      )
    },

    videoThumb(url, size) {
      const matched = url.match(
        /(http:|https:|)\/\/(player.|www.|m.)?(vimeo\.com|youtu(be\.com|\.be|be\.googleapis\.com))\/(video\/|embed\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(\&\S+)?/
      )

      const id = RegExp.$6

      let thumbUrl = ''

      if (RegExp.$3.indexOf('youtu') > -1) {
        thumbUrl = 'https://img.youtube.com/vi/' + id + '/0.jpg'
      } else if (RegExp.$3.indexOf('vimeo') > -1) {
        this.$http.get('https://vimeo.com/api/v2/video/' + id + '.json').then((response) => {
          const imgSrc = response.data[0].thumbnail_large
          thumbUrl = imgSrc
        })
      }
      return thumbUrl
    },
    br(text) {
      return text.replace(/\n|\r/gim, '<br/>')
    },
    scrollTrigger() {
      if (!document || !window) return
      const event = document.createEvent('HTMLEvents')
      event.initEvent('scroll', true, false)
      window.dispatchEvent(event)
    },
    scrollTo(offset = 0) {
      window &&
        window.scrollTo({
          behavior: 'smooth',
          left: 0,
          top: this.$refs.scrollTarget.offsetTop - offset,
        })
    },
    getOffsetTop(element) {
      let yPosition = 0
      let nextElement = element

      while (nextElement) {
        yPosition += nextElement.offsetTop
        nextElement = nextElement.offsetParent
      }

      return yPosition
    },

    getScrollTop() {
      if (typeof pageYOffset != 'undefined') {
        //most browsers except IE before #9
        return pageYOffset
      } else {
        var B = document.body //IE 'quirks'
        var D = document.documentElement //IE with doctype
        D = D.clientHeight ? D : B
        return D.scrollTop
      }
    },

    scrollThan(ref) {
      if (!window) return

      const refTop = ref.offsetTop

      if (this.getScrollTop() > refTop - 150) {
        window.scrollTo({
          behavior: 'smooth',
          left: 0,
          top: refTop - this.$store.getters.topBanner - 20,
        })
      }
    },
    parseList(content) {
      const contents = content.split('\\n')
      let returnContent = contents.reduce((str, line) => `${str}<li>${line}</li>`, ``)
      return returnContent
    },
    catchError(error) {
      this.$message({
        message: this.$t('common.message.tempError'),
        type: 'error',
      })
      if (error.config) {
        console.error(`[${error.status}] ${error.statusText}, url: ${error.config.url}`)
      }
    },
    koreanSuffix(string) {
      // 한글 마지막글자 받침여부
      string = string.charCodeAt(string.length - 1)
      return (string - 0xac00) % 28 > 0
    },
    //준비중인 경우 (상품문의, 실시간문의)
    alertComingSoon(toInquiry) {
      this.$$alert(
        this.$t('common.comingSoon'),
        toInquiry
          ? {
              callback: (action) => {
                // if (action) this.$router.push('/' + this.$i18n.locale + '/account/content/inquiry')
              },
            }
          : {}
      )
    },
    maintenanceAlert() {
      this.$$alert(
        '현재 원활한 서비스를 위한 준비 중입니다.<br>빠른 시일 내에 좋은 모습으로 찾아 뵙겠습니다.'
      )
    },
  },
  mounted() {
    if (this.$metaInfo) {
      const title = this.$metaInfo.title
      this.$ga.page({
        page: this.$route.fullPath,
        title: title,
        location: window.location.href,
      })
      fbq('track', 'PageView')
    }
  },
}

export default mixins
